import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config';
import { useGamificationContext } from '../../context/GamificationContext';

// components
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import StockSearch from '../../components/Common/StockSearch';
import { useScreenType } from '../../hooks/useScreenType';
import DesktopTransactions from './DesktopTransactions';
import MobileTransactions from './MobileTransactions';

interface Stock {
  symbole: string;
  nom_societe: string;
}

interface Cotation {
  cours_cloture: number;
  date_cotation: string;
}

interface Transaction {
  id: number;
  transaction_type: string;
  stock_symbol: string;
  quantity: number;
  price_per_unit: number;
  total_amount: number;
  transaction_date: string;
  status: string;
}

interface Portfolio {
  stock_symbol: string;
  quantity: number;
}

const TransactionsScreen: React.FC = () => {
  const { token, user, refreshUser } = useAuth();
  const { trackAction } = useGamificationContext();
  const [activeTab, setActiveTabBrut] = useState<'buy' | 'sell' | 'history'>('buy');
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [portfolio, setPortfolio] = useState<Portfolio[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [balance, setBalance] = useState<number>(0);

  // Form state
  const [selectedStock, setSelectedStock] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Initialize feeRate from user profile, default to 1.1 if not set
  const [feeRate, setFeeRate] = useState<string>('1.1');

  useEffect(() => {
    if (user?.transaction_fee_rate !== undefined) {
      setFeeRate(user.transaction_fee_rate.toString());
    } else {
      // Fallback to localStorage for smooth transition or default
      const stored = localStorage.getItem('transactionFeeRate');
      if (stored) setFeeRate(stored);
    }
  }, [user]);

  // Save feeRate to backend on blur
  const saveFeeRate = async () => {
    if (!token) return;
    try {
      const rateToCheck = parseFloat(feeRate);
      // Only save if valid and different from current user value (to avoid spam)
      if (!isNaN(rateToCheck) && rateToCheck !== user?.transaction_fee_rate) {
        await fetch(`${API_URL}/auth/me/preferences`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ transaction_fee_rate: rateToCheck }),
        });
        // Refresh user context to update the fee rate locally
        await refreshUser();
      }
    } catch (err) {
      console.error('Failed to save fee preference:', err);
    }
  };

  // History filter states
  const [filterType, setFilterType] = useState<'ALL' | 'BUY' | 'SELL'>('ALL');
  const [filterSymbol, setFilterSymbol] = useState<string>('');
  const [filterDateFrom, setFilterDateFrom] = useState<string>('');
  const [filterDateTo, setFilterDateTo] = useState<string>('');

  useEffect(() => {
    if (token) {
      fetchStocks();
      fetchPortfolio();
      fetchBalance();
      if (activeTab === 'history') {
        fetchTransactions();
      }
    }
  }, [activeTab, token]);

  // Log stocks state changes
  useEffect(() => {
    console.log('[TransactionsScreen] stocks state changed, length:', stocks.length);
  }, [stocks]);

  const fetchStocks = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/stocks/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        console.log('[TransactionsScreen] Fetched active stocks:', data.length);
        setStocks(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('[TransactionsScreen] Error fetching stocks:', err);
    }
  };

  const fetchPortfolio = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portfolio`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPortfolio(data);
      }
    } catch (err) {
      console.error('Error fetching portfolio:', err);
    }
  };

  const fetchBalance = async () => {
    try {
      const response = await fetch(`${API_URL}/api/account/balance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setBalance(data.balance);
      }
    } catch (err) {
      console.error('Error fetching balance:', err);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/transactions/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
    }
  };

  const fetchLastPrice = async (symbole: string) => {
    try {
      const response = await fetch(`${API_URL}/v1/stocks/${symbole}/history?limit=1`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data: Cotation[] = await response.json();
        if (data.length > 0 && data[0].cours_cloture) {
          setPrice(data[0].cours_cloture.toString());
        }
      }
    } catch (err) {
      console.error('Error fetching last price:', err);
    }
  };

  const handleStockChange = (symbole: string) => {
    setSelectedStock(symbole);
    if (symbole) {
      fetchLastPrice(symbole);
    } else {
      setPrice('');
    }
  };

  const handleBuy = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const qty = parseInt(quantity);
    const rawPrice = parseFloat(price);
    const rateVal = parseFloat(feeRate) || 0;

    if (
      !selectedStock ||
      isNaN(qty) ||
      qty <= 0 ||
      isNaN(rawPrice) ||
      rawPrice <= 0 ||
      !date
    ) {
      setError('Veuillez remplir tous les champs correctement');
      return;
    }

    const stock = stocks.find((s) => s.symbole === selectedStock);
    if (!stock) return;

    const feesAmount = (qty * rawPrice) * (rateVal / 100);

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/transactions/buy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          transaction_type: 'BUY',
          stock_symbol: stock.symbole,
          quantity: qty,
          price_per_unit: rawPrice,
          fees: feesAmount,
          fee_rate: rateVal,
          transaction_date: new Date(date).toISOString(),
        }),
      });

      if (response.ok) {
        setSuccess(
          `Achat réussi: ${qty} actions ${selectedStock} à ${rawPrice.toFixed(2)} FCFA (+ ${rateVal}% frais) le ${date}`
        );
        setSelectedStock('');
        setQuantity('');
        setPrice('');
        setDate(new Date().toISOString().split('T')[0]);
        fetchPortfolio();
        fetchBalance();
        // trackAction('ADD_PORTFOLIO_POSITION');
      } else {
        const errorData = await response.json();
        setError(errorData.detail || "Erreur lors de l'achat");
      }
    } catch (err) {
      setError('Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  const handleSell = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const qty = parseInt(quantity);
    const rawPrice = parseFloat(price);
    const rateVal = parseFloat(feeRate) || 0;

    if (
      !selectedStock ||
      isNaN(qty) ||
      qty <= 0 ||
      isNaN(rawPrice) ||
      rawPrice <= 0 ||
      !date
    ) {
      setError('Veuillez remplir tous les champs correctement');
      return;
    }

    // Removed strict check against 'stocks' list to allow selling any portfolio item
    // const stock = stocks.find((s) => s.nom_societe === selectedStock);
    // if (!stock) return;

    const feesAmount = (qty * rawPrice) * (rateVal / 100);

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/transactions/sell`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          transaction_type: 'SELL',
          stock_symbol: selectedStock,
          quantity: qty,
          price_per_unit: rawPrice,
          fees: feesAmount,
          fee_rate: rateVal,
          transaction_date: new Date(date).toISOString(),
        }),
      });

      if (response.ok) {
        setSuccess(
          `Vente réussie: ${qty} actions ${selectedStock} à ${rawPrice.toFixed(2)} FCFA (net de ${rateVal}% frais) le ${date}`
        );
        setSelectedStock('');
        setQuantity('');
        setPrice('');
        setDate(new Date().toISOString().split('T')[0]);
        fetchPortfolio();
        fetchBalance();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Erreur lors de la vente');
      }
    } catch (err) {
      setError('Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: string = 'csv') => {
    try {
      let url = `${API_URL}/api/reports/transactions/export?format=${format}`;
      if (filterDateFrom) url += `&start_date=${filterDateFrom}`;
      if (filterDateTo) url += `&end_date=${filterDateTo}`;

      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transactions_${new Date().toISOString().split('T')[0]}_${filterDateFrom || 'all'}.${format}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        console.error('Export failed');
        // Optional: setError('Export failed');
      }
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const rawPriceVal = parseFloat(price) || 0;
  const quantityVal = parseInt(quantity) || 0;
  const rateVal = parseFloat(feeRate) || 0;

  // Calculate final unit price (PRU or Net)
  const finalUnitPrice = activeTab === 'buy'
    ? rawPriceVal * (1 + rateVal / 100)
    : rawPriceVal * (1 - rateVal / 100);

  const totalCost = (quantityVal * finalUnitPrice).toLocaleString(undefined, { maximumFractionDigits: 0 });
  const selectedHolding = portfolio.find((p) => p.stock_symbol === selectedStock);

  const { isMobile } = useScreenType();

  // Changer d'onglet repart d'un formulaire vide : une quantité ou une erreur d'achat ne doit pas suivre vers la vente.
  const setActiveTab = (tab: 'buy' | 'sell' | 'history') => {
    setActiveTabBrut(tab);
    setSelectedStock('');
    setQuantity('');
    setPrice('');
    setError('');
    setSuccess('');
  };

  const childProps = {
    activeTab, setActiveTab, balance, stocks, portfolio, transactions,
    selectedStock, quantity, price, date, loading, error, success, feeRate, totalCost, finalUnitPrice, selectedHolding,
    setDate, handleStockChange, setQuantity, setPrice, setFeeRate, saveFeeRate, handleBuy, handleSell, handleExport,
    filterType, setFilterType, filterSymbol, setFilterSymbol, filterDateFrom, setFilterDateFrom, filterDateTo, setFilterDateTo,
    apiUrl: API_URL,
    token: token || '',
    onImportSuccess: fetchTransactions,
  };

  return (
    <SiteLayout>
      <Header icon='swap_horiz' title='Transactions' />
      {isMobile ? (
        <MobileTransactions {...childProps} />
      ) : (
        <DesktopTransactions {...childProps} />
      )}
    </SiteLayout>
  );
};

export default TransactionsScreen;
